import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, FileText, Download } from "lucide-react";
import { SEO, SITE_URL } from "@/components/SEO";
import { Reveal } from "@/components/Section";
import { getIssueBySlug } from "@/lib/newsletterData";
import { CitedText } from "@/components/newsletter/CitedText";
import { ShareButtons } from "@/components/newsletter/ShareButtons";

export default function NewsletterArticle() {
  const { slug } = useParams();
  const issue = getIssueBySlug(slug);

  if (!issue) return <Navigate to="/newsletter" replace />;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="newsletter-article-page">
      <SEO
        title={`${issue.title}: WearSpray Newsletter`}
        description={issue.excerpt}
        path={`/newsletter/${issue.slug}`}
        image={`${SITE_URL}${issue.ogImage || "/og/wearspray-newsletter-personalization.jpg"}`}
      />

      <article className="max-w-2xl mx-auto px-6 md:px-10 pt-32 md:pt-40 pb-24 md:pb-32">
        <Reveal>
          <Link to="/newsletter" data-testid="newsletter-back-to-hub" className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.15em] uppercase text-zinc-500 hover:text-accent transition-colors">
            <ArrowLeft size={13} /> WearSpray Newsletter
          </Link>
          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-light tracking-tight leading-[1.1] mt-6 text-white">
            {issue.title}
          </h1>
          <p className="font-body text-zinc-400 text-lg leading-relaxed mt-5">{issue.dek}</p>
          <div className="flex items-center gap-3 mt-6 font-mono text-xs tracking-[0.1em] text-zinc-500">
            <span>{issue.author}</span>
            <span className="text-zinc-700">&middot;</span>
            <span>{issue.date}</span>
          </div>
          <ShareButtons title={issue.title} className="mt-6" testIdPrefix="newsletter-share-top" />
        </Reveal>

        {issue.disclaimer && (
          <Reveal delay={0.05}>
            <div data-testid="newsletter-disclaimer" className="border border-accent/25 bg-accent/[0.04] rounded-xl p-5 mt-8">
              <p className="font-body text-sm text-zinc-300 leading-relaxed italic">{issue.disclaimer}</p>
            </div>
          </Reveal>
        )}

        <div className="space-y-10 mt-14">
          {issue.sections.map((section, i) => (
            <Reveal key={section.heading} delay={Math.min(i * 0.04, 0.3)}>
              <h2 className="font-heading text-xl md:text-2xl font-medium text-white mb-4">{section.heading}</h2>
              <div className="space-y-4">
                {section.paragraphs?.map((p, j) => (
                  <CitedText key={j} text={p} className="font-body text-[17px] text-zinc-300 leading-[1.8]" />
                ))}
                {section.bullets?.length > 0 && (
                  <ul className="space-y-2.5">
                    {section.bullets.map((b, j) => (
                      <li key={j} className="flex items-start gap-3">
                        <span className="w-1.5 h-1.5 rounded-full bg-accent mt-2.5 shrink-0" />
                        <CitedText text={b} className="font-body text-[17px] text-zinc-300 leading-[1.8]" />
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </Reveal>
          ))}
        </div>

        {issue.downloadPdf && (
          <Reveal>
            <a
              href={issue.downloadPdf.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="newsletter-download-pdf"
              className="group flex items-center gap-4 text-left bg-zinc-950 border border-zinc-800 rounded-2xl p-5 mt-14 hover:border-accent/50 transition-colors"
            >
              <div className="w-11 h-11 rounded-xl bg-accent/10 flex items-center justify-center text-accent shrink-0">
                <FileText size={20} strokeWidth={1.75} />
              </div>
              <div className="flex-1">
                <p className="font-body text-sm font-semibold text-white">{issue.downloadPdf.label}</p>
                <p className="font-body text-xs text-zinc-500 mt-0.5">{issue.downloadPdf.description}</p>
              </div>
              <Download size={16} className="text-zinc-500 group-hover:text-accent transition-colors shrink-0" />
            </a>
          </Reveal>
        )}

        {issue.sources?.length > 0 && (
          <Reveal>
            <div className="border-t border-zinc-800 mt-16 pt-10">
              <h3 className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 mb-5">Sources and Further Reading</h3>
              <ol className="space-y-3" data-testid="newsletter-sources-list">
                {issue.sources.map((s, i) => (
                  <li key={i} id={`source-${i + 1}`} className="font-body text-sm text-zinc-500 leading-relaxed flex gap-2.5">
                    <span className="text-zinc-600 shrink-0">[{i + 1}]</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ol>
            </div>
          </Reveal>
        )}

        <Reveal>
          {issue.inviteNote && (
            <div data-testid="newsletter-invite-note" className="border border-accent/30 bg-accent/[0.06] rounded-xl p-5 mt-16 mb-8">
              <p className="font-body text-sm text-zinc-200 leading-relaxed">{issue.inviteNote}</p>
            </div>
          )}
          <div className={`border-t border-zinc-800 pt-10 flex flex-wrap items-center justify-between gap-5 ${issue.inviteNote ? "" : "mt-16"}`}>
            <Link to="/newsletter" className="inline-flex items-center gap-2 font-body text-sm text-zinc-400 hover:text-white transition-colors">
              <ArrowLeft size={14} /> All issues
            </Link>
            <Link
              to={issue.ctaLink || "/iq?src=newsletter"}
              data-testid="newsletter-cta-iq"
              className="inline-flex items-center gap-2 font-body text-sm font-medium bg-accent text-black px-6 py-3.5 hover:bg-white transition-colors"
            >
              {issue.ctaLabel || "Join WearSpray IQ"} <ArrowRight size={14} />
            </Link>
          </div>
          <ShareButtons title={issue.title} className="mt-8 justify-center" testIdPrefix="newsletter-share-bottom" />
        </Reveal>
      </article>
    </div>
  );
}
